import { supabase } from '@/integrations/supabase/client';
import { subscriptionService } from './subscription';

// Usage limits per tier based on landing page
export const USAGE_LIMITS = {
  free: 2,      // 2 AI analyses per month
  pro: 25,      // 25 AI-powered insights per month  
  expert: -1    // Unlimited for expert tier
};

export const usageTrackingService = {
  async getCurrentLimit(): Promise<number> {
    try {
      const subscription = await subscriptionService.getCurrentSubscription();
      const tier = subscription?.subscription_tier || 'free';
      return USAGE_LIMITS[tier as keyof typeof USAGE_LIMITS] || USAGE_LIMITS.free;
    } catch (error) {
      console.error('Error getting usage limit:', error);
      return USAGE_LIMITS.free;
    }
  },

  async getCurrentUsage(): Promise<number> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.rpc('get_current_analysis_usage', {
        user_uuid: user.user.id
      });

      if (error) {
        console.error('Error getting current usage:', error);
        throw error;
      }

      return data || 0;
    } catch (error) {
      console.error('Error getting usage:', error);
      return 0;
    }
  },

  async incrementUsage(): Promise<number> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.rpc('increment_analysis_usage', {
        user_uuid: user.user.id
      });

      if (error) {
        console.error('Error incrementing usage:', error);
        throw error;
      }

      return data || 0;
    } catch (error) {
      console.error('Error incrementing usage:', error);
      throw error;
    }
  },

  async checkCanRunAnalysis(): Promise<{ canRun: boolean; currentUsage: number; limit: number }> {
    const currentUsage = await this.getCurrentUsage();
    const limit = await this.getCurrentLimit();
    
    // Expert tier has unlimited usage (limit = -1)
    const canRun = limit === -1 || currentUsage < limit;
    
    return {
      canRun,
      currentUsage,
      limit: limit === -1 ? Infinity : limit
    };
  }
};